/**
 * Session boot — the app-side, impure edge of the spine (architecture.md §1,
 * §9): choose the host, create the store, open the session.
 *
 * @remarks
 * Nondeterminism lives HERE by design (the isomorphic boundary): the seed
 * comes from `?seed=` when the URL provides a finite one — a shareable,
 * replayable session — and from `Date.now()` otherwise; the local host is
 * handed both as functions, and never reads a clock itself.
 *
 * D156: two hosts behind one contract. Without `VITE_PLAY_URL` the build is
 * `play: 'client'` — the shipped pack is a STATIC import Vite inlines at
 * build time (D189: `virtual:shipped-pack`, whichever pack `VITE_PACK`
 * named), the local host runs every turn here, saves to IndexedDB, and
 * the analytics reporter watches its commits. With it, the build is
 * `play: 'server'`: no pack ships at all, the remote host asks
 * `services/play-api` for every turn, and the session id is remembered in
 * `localStorage` so a reload resumes. The store and the shell are the same
 * in both; only this module knows which it is. D157 adds the third
 * variable: `VITE_IDENTITY_URL` names the identity Worker, and with it the
 * shell gets an account control; without it, no such button exists. D162
 * makes the app ONE app: with a play Worker and no `VITE_GAME_ID`, the
 * hostname selects the game, in whichever mode the game declares — and
 * D198 lets a DEMO hostname hold several under paths, its root sending
 * the browser to a landing page (Mike: hostnames for real games, paths
 * for demos). D241: every host is handed the game's Record store
 * (`record-storage.ts`) — the account's when the player is signed in, the
 * device's when they are not, merged at the first signed-in boot.
 *
 * @packageDocumentation
 */

import { type GameDescription, hostAnswerSchema, type TurnOutcome } from '@orpheus/api-contracts';
import { createLocalJsonSource, describeLoadFailure, type LoadedGameBundle, loadGameBundle } from '@orpheus/content-loader';
import standardEnglishVocabularyDocument from '@orpheus/content-packs/standard-english-vocabulary.json';
import { vocabularyPackDefinitionSchema } from '@orpheus/content-schema';
import { createGameStore, type GameStore, openSession } from '@orpheus/game-store';
import {
	createKeyValueRecordStorage,
	createLocalHost,
	createRemoteHost,
	type LocalHost,
	type PlayHost,
	type RecordStorageAdapter,
	type SaveStorageAdapter,
	type SessionMemory,
} from '@orpheus/player-host';
import shippedPackDocument, { assetBaseUrl as shippedPackAssetBaseUrl } from 'virtual:shipped-pack';

import { connectAnalyticsReporter } from './analytics-reporter.js';
import {
	type BearerSessionStore,
	createBearerSessionStore,
	handoffDepartureUrl,
	handoffRequestOf,
	handoffTokenOf,
	isForeignHostname,
	withBearerSession,
} from './handoff.js';
import { createIdentityClient, type IdentityClient } from './identity-client.js';
import { connectAudioPreferencePersistence } from './preference-storage.js';
import { createRecordDoorsClient } from './record-doors-client.js';
import { createShellRecordStorage } from './record-storage.js';
import { createIndexedDbSaveStorage } from './save-storage.js';
import { createStoreClient, type StoreClient } from './store-client.js';

/**
 * D170: what the shell needs to know about the handoff, on either end of
 * it. Both empty on a home page that was not asked to hand off — the
 * ordinary case.
 */
export interface HandoffContext {
	/** On a foreign hostname with a handoff page configured: where Sign in sends the player. */
	readonly departureUrl?: string;
	/** On a foreign hostname: the token this page arrived with did not exchange — said once, in the account dialog. */
	readonly refusal?: string;
	/** On the home host: the address this page was asked to hand a signed-in player back to. */
	readonly returnUrl?: string;
}

/** What `bootSession` hands the shell. */
export interface BootedSession {
	/** The loaded bundle — in client mode only; a server game's client never holds one. */
	readonly bundle?: LoadedGameBundle;
	readonly game: GameDescription;
	/** D170: the handoff, as this page sees it. */
	readonly handoff: HandoffContext;
	readonly host: PlayHost;
	/** The identity Worker's client (D157) — only when the build names one (`VITE_IDENTITY_URL`). */
	readonly identity?: IdentityClient;
	readonly sessionSeed: number;
	readonly store: GameStore;
	/** The store Worker's client (D165) — only when the build names one (`VITE_STORE_URL`); absent, a paywall has no button. */
	readonly storefront?: StoreClient;
	readonly wasResumed: boolean;
}

/** The edges `bootSession` reads from the build and the browser, each replaceable by a test. */
export interface BootSessionOptions {
	/** The analytics Worker's events URL — `VITE_ANALYTICS_URL` by default; absent, the reporter is inert. */
	readonly analyticsEndpoint?: string | undefined;
	/** D170: where a foreign hostname keeps its bearer session — `window.localStorage` by default. */
	readonly bearerStorage?: Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;
	readonly fetchImplementation?: typeof fetch;
	/** D170: the home page that performs handoffs — `VITE_HANDOFF_URL` by default; absent, a foreign hostname has no way to sign in. */
	readonly handoffUrl?: string | undefined;
	/** D170: the page's `location.hash` on arrival — where a handoff token rides. */
	readonly hashString?: string;
	/** The hostname the app is served on — `window.location.hostname` by default; with a play Worker and no `VITE_GAME_ID`, it selects the game (D162). */
	readonly hostname?: string;
	/** D198: where the browser is sent when the hostname's root answers a landing page — `window.location.replace` by default. */
	readonly leaveFor?: (landing: string) => void;
	/** D198: the page's `location.pathname` — its first segment selects a demo under the hostname; `window.location.pathname` by default. */
	readonly pathname?: string;
	/** D170: the identity's cookie domain — `VITE_IDENTITY_SITE` by default; a hostname outside it is foreign and hands off. */
	readonly identitySite?: string | undefined;
	/** The identity Worker's origin — `VITE_IDENTITY_URL` by default; absent, there is no account control at all. */
	readonly identityUrl?: string | undefined;
	/** D170: called once a handoff token has been read off the arrival, so it leaves the address bar before anything else runs. */
	readonly onHandoffArrived?: () => void;
	/** D170: the address this page is returned to after a handoff — `location` without its fragment, by default. */
	readonly returnUrl?: string;
	/** The play Worker's origin — `VITE_PLAY_URL` by default; absent, the game runs here. */
	readonly playUrl?: string | undefined;
	/** D264: handed every turn's outcome on its way to the store — the shell's ear, which plays the sounds its lines made. */
	readonly hearTurn?: (outcome: TurnOutcome) => void;
	/** D241: where the Record is kept — by default the shell's own (`record-storage.ts`): the account when signed in, else this device. */
	readonly recordStorage?: RecordStorageAdapter;
	readonly saveStorage?: SaveStorageAdapter;
	readonly sessionMemory?: SessionMemory;
	/** The store Worker's origin — `VITE_STORE_URL` by default; absent, nothing is for sale from this build. */
	readonly storeUrl?: string | undefined;
}

/**
 * D264: a host whose every turn passes the shell's ear on its way to the store. The input line, a chip and the clock's
 * wait all submit through the host, so no door can forget the sound; opening and restoring a session never pass here.
 *
 * @param host - The host the store would otherwise play through
 * @param hearTurn - The ear
 * @returns The same host, heard
 */
export const hearingHost = (host: PlayHost, hearTurn: (outcome: TurnOutcome) => void): PlayHost => ({
	...host,
	submit: async (input) => {
		const outcome = await host.submit(input);

		hearTurn(outcome);

		return outcome;
	},
});

/**
 * Boot a session: choose the host by the build, create the store, open.
 *
 * @param searchString - The page's `location.search` (for `?seed=`)
 * @param options - {@link BootSessionOptions}
 * @returns The booted session
 */
export const bootSession = async (searchString: string, options: BootSessionOptions = {}): Promise<BootedSession> => {
	const seed = resolveExplicitSeed(searchString);
	const playUrl = options.playUrl === undefined ? readEnvironment('VITE_PLAY_URL') : options.playUrl;
	const identityUrl = 'identityUrl' in options ? options.identityUrl : readEnvironment('VITE_IDENTITY_URL');
	// D170: on a foreign hostname the account is a bearer session, carried by
	// the ONE fetch every client below is built on; the handoff token this
	// page may have arrived with is exchanged before the session opens, so
	// the play Worker sees the account from the first request.
	const away = identityUrl === undefined ? undefined : await arriveFromHandoff(identityUrl, options);
	const edge: BootSessionOptions = away === undefined ? options : { ...options, fetchImplementation: away.fetchImplementation };
	const returnAsked = away === undefined ? handoffRequestOf(searchString) : undefined;
	const identity = away?.identity ?? createIdentityFor(identityUrl, edge, returnAsked);
	const storeUrl = 'storeUrl' in options ? options.storeUrl : readEnvironment('VITE_STORE_URL');
	const storefront = createStorefrontFor(storeUrl, edge);
	// D241: the Record's store, made per play mode — a server-run game's account copy is the play Worker's to read.
	const records: RecordStoreFor = (isHeldByServer) =>
		edge.recordStorage ??
		createShellRecordStorage({
			...(storeUrl === undefined
				? {}
				: {
					account: createRecordDoorsClient({
						baseUrl: storeUrl.replace(/\/$/u, ''),
						...(edge.fetchImplementation === undefined ? {} : { fetchImplementation: edge.fetchImplementation }),
					}),
				}),
			device: createKeyValueRecordStorage(() => window.localStorage),
			isHeldByServer,
			isSignedIn: async () => (await identity?.readSession()) !== undefined,
		});
	const { bundle, host } =
		playUrl === undefined ? await createShippedHost(seed, edge, records) : await createPlatformHost(playUrl.replace(/\/$/u, ''), seed, edge, records);
	const heardHost = options.hearTurn === undefined ? host : hearingHost(host, options.hearTurn);
	const store = createGameStore({
		host: heardHost,
		// D132: the DevTools hook is a development convenience and a published
		// game's open book — the production build never offers it.
		isDevToolsEnabled: import.meta.env.DEV,
	});

	/*
	 * D118: audio preferences survive the reload — hydrated from localStorage
	 * before anything renders, mirrored back on every change. Deliberately
	 * OUTSIDE the seed ruling: `?seed=` resets the session, never the
	 * player's volume.
	 */
	connectAudioPreferencePersistence(store);

	const opened = await store.dispatch(openSession());

	return {
		...(bundle === undefined ? {} : { bundle }),
		game: opened.game,
		handoff: handoffContextOf(away, returnAsked),
		host: heardHost,
		...(identity === undefined ? {} : { identity }),
		sessionSeed: opened.seed,
		store,
		...(storefront === undefined ? {} : { storefront }),
		wasResumed: opened.wasResumed,
	};
};

/**
 * D157: the identity client, when the build names an identity Worker. Every
 * door lands back on this page, without its query — `?seed=` is a session's
 * instruction, not an account's. D170: except the handoff's own ask, which
 * must survive the trip through a door, or a player who signed in by link
 * would land back on the home host with nowhere to be returned to.
 */
const createIdentityFor = (
	identityUrl: string | undefined,
	options: BootSessionOptions,
	returnAsked: string | undefined,
	bearer?: BearerSessionStore,
): IdentityClient | undefined =>
	identityUrl === undefined
		? undefined
		: createIdentityClient({
			baseUrl: identityUrl.replace(/\/$/u, ''),
			...(bearer === undefined ? {} : { bearer }),
			callbackUrl:
				returnAsked === undefined
					? `${window.location.origin}${window.location.pathname}`
					: `${window.location.origin}${window.location.pathname}?${new URLSearchParams({ handoff: returnAsked }).toString()}`,
			...(options.fetchImplementation === undefined ? {} : { fetchImplementation: options.fetchImplementation }),
		});

/** D165: the store client, when the build names a store Worker. */
const createStorefrontFor = (storeUrl: string | undefined, options: BootSessionOptions): StoreClient | undefined =>
	storeUrl === undefined
		? undefined
		: createStoreClient({
			baseUrl: storeUrl.replace(/\/$/u, ''),
			...(options.fetchImplementation === undefined ? {} : { fetchImplementation: options.fetchImplementation }),
		});

/** D170: the handoff as the shell will see it — the foreign side's two facts, or the home side's one. */
const handoffContextOf = (away: Away | undefined, returnAsked: string | undefined): HandoffContext => ({
	...(away?.departureUrl === undefined ? {} : { departureUrl: away.departureUrl }),
	...(away?.refusal === undefined ? {} : { refusal: away.refusal }),
	...(returnAsked === undefined ? {} : { returnUrl: returnAsked }),
});

/** What a foreign hostname boots with (D170): its bearer-carrying fetch, its identity client, and the handoff's two facts. */
interface Away {
	readonly departureUrl?: string;
	readonly fetchImplementation: typeof fetch;
	readonly identity: IdentityClient;
	readonly refusal?: string;
}

/**
 * D170: on a foreign hostname, the bearer session and the arrival. The
 * token, when the fragment carries one, leaves the address bar before it
 * is spent — a reload must not spend it twice, and a copied link must not
 * carry it. Nothing on a home hostname.
 */
const arriveFromHandoff = async (identityUrl: string, options: BootSessionOptions): Promise<Away | undefined> => {
	const identitySite = 'identitySite' in options ? options.identitySite : readEnvironment('VITE_IDENTITY_SITE');
	const hostname = options.hostname ?? window.location.hostname;

	if (!isForeignHostname(hostname, identitySite)) {
		return undefined;
	}

	const bearer = createBearerSessionStore(options.bearerStorage ?? window.localStorage);
	const fetchImplementation = withBearerSession(bearer, options.fetchImplementation ?? fetch.bind(globalThis));
	const identity = createIdentityFor(identityUrl, { ...options, fetchImplementation }, undefined, bearer);
	const handoffUrl = 'handoffUrl' in options ? options.handoffUrl : readEnvironment('VITE_HANDOFF_URL');
	const returnUrl = options.returnUrl ?? `${window.location.origin}${window.location.pathname}${window.location.search}`;
	const token = handoffTokenOf(options.hashString ?? window.location.hash);
	let refusal: string | undefined;

	if (token !== undefined) {
		(options.onHandoffArrived ?? dropFragment)();

		const accepted = await identity?.acceptHandoff(token);

		refusal = accepted?.kind === 'refused' ? accepted.reason : undefined;
	}

	if (identity === undefined) {
		return undefined;
	}

	return {
		...(handoffUrl === undefined ? {} : { departureUrl: handoffDepartureUrl(handoffUrl, returnUrl) }),
		fetchImplementation,
		identity,
		...(refusal === undefined ? {} : { refusal }),
	};
};

/** The default arrival: the fragment leaves the address bar, and the history entry is replaced so Back does not bring it back. */
const dropFragment = (): void => {
	window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
};

/** D241: the Record's store for a host — whether the play Worker holds the account's copy itself (a server-run game). */
type RecordStoreFor = (isHeldByServer: boolean) => RecordStorageAdapter;

/** Client mode on the shipped pack: the static import Vite inlined (D189: whichever pack the build named), its assets beside it. */
const createShippedHost = async (seed: number | undefined, options: BootSessionOptions, records: RecordStoreFor) =>
	createClientHost(await loadClientBundle(shippedPackDocument, `static import: ${shippedPackAssetBaseUrl} (player-web)`, shippedPackAssetBaseUrl), seed, options, records(false));

/**
 * D162: the platform names the game. A build that names one (`VITE_GAME_ID`)
 * is a server-mode build of that game, as §10.3 documents; a build that
 * names none is the ONE player app, and the hostname it is served on
 * selects the game — `play: 'server'` games get the remote host, `client`
 * games fetch their bundle by its hash and run it here, saves and reporter
 * and all, exactly as the shipped pack does.
 */
const createPlatformHost = async (
	baseUrl: string,
	seed: number | undefined,
	options: BootSessionOptions,
	records: RecordStoreFor,
): Promise<{ readonly bundle?: LoadedGameBundle; readonly host: PlayHost }> => {
	const fetchImplementation = options.fetchImplementation ?? fetch.bind(globalThis);
	const namedGameId = readEnvironment('VITE_GAME_ID');
	const remoteHostFor = (gameId: string): PlayHost =>
		createRemoteHost({
			baseUrl,
			fetch: fetchImplementation,
			gameId,
			recordStorage: records(true),
			sessionMemory: options.sessionMemory ?? createLocalStorageSessionMemory(),
			...(seed === undefined ? {} : { seed }),
		});

	if (namedGameId !== undefined) {
		return { host: remoteHostFor(namedGameId) };
	}

	const hostname = options.hostname ?? window.location.hostname;
	const address = servedAddressOf(hostname, options.pathname ?? window.location.pathname);
	const answer = await fetchImplementation(`${baseUrl}/hosts/${address}`);

	if (!answer.ok) {
		throw new Error(`No game is published at ${address} (the play Worker answered ${String(answer.status)})`);
	}

	const served = hostAnswerSchema.parse(await answer.json());

	// D198: this root serves no game; the browser is leaving for the landing page, and a page that is leaving has no
	// session to boot — the promise never settles, so nothing below it runs against a page that is about to be gone.
	if ('landing' in served) {
		(options.leaveFor ?? leaveFor)(served.landing);

		return new Promise<never>(() => undefined);
	}

	if (served.game.play === 'server') {
		return { host: remoteHostFor(served.gameId) };
	}

	// A client game: the bundle by its hash — immutable, so the browser may keep it as long as it likes.
	const bundleByHash = async (hash: string): Promise<LoadedGameBundle> => {
		const bundleAnswer = await fetchImplementation(`${baseUrl}/bundles/${encodeURIComponent(served.gameId)}/${hash}`);

		if (!bundleAnswer.ok) {
			throw new Error(`The bundle for ${served.gameId} could not be fetched (the play Worker answered ${String(bundleAnswer.status)})`);
		}

		return loadClientBundle(await bundleAnswer.json(), `bundle store: ${served.gameId}@${hash.slice(0, 12)}`, served.assetBaseUrl);
	};

	// D164: the host knows which version it is, and can reach the one a save was played on — the same door, an older hash.
	return createClientHost(await bundleByHash(served.bundleHash), seed, options, records(false), { bundleHash: served.bundleHash, bundleOf: bundleByHash });
};

/**
 * D198: the address the hosts door is asked about — the hostname, and the
 * path's first segment when there is one. Everything past the first
 * segment is the page's own business (nothing today), never a second game.
 */
const servedAddressOf = (hostname: string, pathname: string): string => {
	const [segment] = pathname.split('/').filter((part) => part !== '');

	return segment === undefined ? encodeURIComponent(hostname) : `${encodeURIComponent(hostname)}/${encodeURIComponent(segment)}`;
};

/** The default departure: the landing replaces this entry, so Back does not return to a root that only sends you on. */
const leaveFor = (landing: string): void => {
	window.location.replace(landing);
};

/** A pack document through the airlock, with the base vocabulary the shipped packs extend. */
const loadClientBundle = async (document: unknown, sourceName: string, assetBaseUrl: string | undefined): Promise<LoadedGameBundle> => {
	const baseDocument = vocabularyPackDefinitionSchema.parse(standardEnglishVocabularyDocument);
	const loadOutcome = await loadGameBundle(
		createLocalJsonSource(document, sourceName, assetBaseUrl === undefined ? {} : { assetBaseUrl }),
		{ baseVocabularies: { 'standard-english-vocabulary': baseDocument.lexicon }},
	);

	if (loadOutcome.type !== 'loaded') {
		throw new Error(`The pack failed to load: ${loadOutcome.failures.map((failure) => describeLoadFailure(failure)).join('; ')}`);
	}

	return loadOutcome.bundle;
};

/** What a published client game knows that the shipped demo does not (D164): its hash, and the door to an older one. */
interface PublishedEdition {
	readonly bundleHash: string;
	readonly bundleOf: (hash: string) => Promise<LoadedGameBundle>;
}

/** Client mode: a loaded bundle, the local host, IndexedDB saves, the analytics reporter on its commits. */
const createClientHost = (
	bundle: LoadedGameBundle,
	seed: number | undefined,
	options: BootSessionOptions,
	recordStorage: RecordStorageAdapter,
	published?: PublishedEdition,
): { readonly bundle: LoadedGameBundle; readonly host: LocalHost } => {
	const host = createLocalHost({
		bundle,
		...published,
		recordStorage,
		savedAtIso: () => new Date().toISOString(),
		saveStorage: options.saveStorage ?? createIndexedDbSaveStorage(),
		...(seed === undefined ? {} : { seed }),
		seedOf: () => Date.now(),
	});

	/*
	 * D154: the session reports itself — anonymous play events to the
	 * analytics Worker, when the build names one. No `VITE_ANALYTICS_URL`
	 * (every local build, every test) means no reporter at all. Connected
	 * BEFORE the session opens, so the opening is the first thing it sees.
	 */
	connectAnalyticsReporter({
		endpoint: 'analyticsEndpoint' in options ? options.analyticsEndpoint : readEnvironment('VITE_ANALYTICS_URL'),
		host,
	});

	return { bundle, host };
};

/** A build-time variable, when the build set one to something. */
const readEnvironment = (
	name: 'VITE_ANALYTICS_URL' | 'VITE_GAME_ID' | 'VITE_HANDOFF_URL' | 'VITE_IDENTITY_SITE' | 'VITE_IDENTITY_URL' | 'VITE_PLAY_URL' | 'VITE_STORE_URL',
): string | undefined => {
	const configured: unknown = import.meta.env[name];

	return typeof configured === 'string' && configured.length > 0 ? configured : undefined;
};

/** `?seed=<n>`, when the URL carries a finite one — the replay/debugging door, which outranks any save or remembered session. */
const resolveExplicitSeed = (searchString: string): number | undefined => {
	const seedParameter = new URLSearchParams(searchString).get('seed');
	const parsedSeed = seedParameter === null ? Number.NaN : Number(seedParameter);

	return Number.isFinite(parsedSeed) ? parsedSeed : undefined;
};

const SESSION_MEMORY_PREFIX = 'orpheus.play-session.';

/** The remembered session id per game, in `localStorage` — a convenience the browser may lose, never a save. */
const createLocalStorageSessionMemory = (): SessionMemory => ({
	forget: (gameId) => {
		try {
			window.localStorage.removeItem(`${SESSION_MEMORY_PREFIX}${gameId}`);
		} catch {
			// Storage refused: nothing to forget.
		}
	},
	recall: (gameId) => {
		try {
			return window.localStorage.getItem(`${SESSION_MEMORY_PREFIX}${gameId}`) ?? undefined;
		} catch {
			return undefined;
		}
	},
	remember: (gameId, sessionId) => {
		try {
			window.localStorage.setItem(`${SESSION_MEMORY_PREFIX}${gameId}`, sessionId);
		} catch {
			// Storage refused: the next load opens afresh, which is the honest fallback.
		}
	},
});
